import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { useT } from '../lib/i18n'
import { Spinner } from '../ui/Spinner'
import { useAuth } from './AuthContext'

/**
 * Shown while the session hydrates.
 *
 * Not a skeleton — the design system has none, and a skeleton here would imply
 * the shape of the screen we are about to show, which we do not yet know. A
 * centred spinner is the system's loading state (SUAP-0103).
 *
 * It is deliberately not branded or animated beyond the spinner: this is
 * usually one frame, and anything more elaborate is a splash screen the user
 * sees flicker.
 */
function Hydrating() {
  const { t } = useT()
  return (
    <div className="flex min-h-dvh items-center justify-center" role="status" aria-live="polite">
      <Spinner />
      <span className="sr-only">{t('state.loading')}</span>
    </div>
  )
}

/**
 * A route only a signed-in user may see.
 *
 * The intended destination travels in `state.from`, so signing in returns the
 * user to where they were going rather than to the default tab. `replace` keeps
 * the guarded URL out of history — otherwise Back from the sign-in screen lands
 * on the route that just bounced them, and bounces again.
 */
export function ProtectedRoute() {
  const { status } = useAuth()
  const location = useLocation()

  if (status === 'loading') return <Hydrating />
  if (status === 'anonymous') {
    return <Navigate to="/signin" replace state={{ from: location.pathname + location.search }} />
  }
  return <Outlet />
}

/**
 * A route that makes no sense for a signed-in user — sign-in and register.
 *
 * **`/install` is deliberately not one of these.** It is public *and* reachable
 * with an account: the landing CTA arrives there with no session, and the Me tab
 * links there with one (SUAP-0106). Wrapping it here would break the second.
 *
 * The redirect honours `state.from` so the round trip through sign-in returns
 * the user to what they originally asked for.
 */
export function PublicOnlyRoute() {
  const { status } = useAuth()
  const location = useLocation()

  if (status === 'loading') return <Hydrating />
  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from
    return <Navigate to={from ?? '/today'} replace />
  }
  return <Outlet />
}
